// src/dashboard/pages/teacher/ListingAdherents/hooks/useListingAdherents.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getDojos } from "../../../../../services/dojoServices";
import { getGroupsList } from "../../../../../services/groupServices";
import { getSettings } from "../../../../../services/settingsServices";

import {
  addAdherentToGroup,
  deleteAdherent,
  getAllAdherentsListWithGroup,
  getBirthdayAdherents,
  removeAdherentFromGroup,
  updateAdherent,
  updateBlessures,
  updateTeacherNotes,
} from "../../../../../services/adherentServices";

const PAGE_SIZE = 30;

const STOPPED_STATUSES = [
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié et Arrêt",
];

const DEFAULT_FILTERS = {
  search: "",
  selectedDojo: "",
  dojoId: "",

  filterGroup: "",
  groupName: "",

  selectedSeason: "",
  season: "",

  statusFilter: "",
  statutInscription: "",

  sortBy: "alpha",

  hideStopped: true,
  hideLicencieArret: true,

  onlyBlessed: false,
  onlyWithoutPhoto: false,
};

function seasonNeighbors(season) {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(season || ""));

  if (!match) {
    return { prev: "", next: "" };
  }

  const start = Number(match[1]);
  const end = Number(match[2]);

  return {
    prev: `${start - 1}-${end - 1}`,
    next: `${start + 1}-${end + 1}`,
  };
}

function calculateAge(dateNaissance) {
  if (!dateNaissance) return null;

  const birth = new Date(dateNaissance);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();

  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age;
}

function getGroupsLabel(adherent) {
  return (adherent?.Groups || [])
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
}

function hasUsablePhoto(adherent) {
  if (!adherent) return false;
  if (adherent.autorisation_photo === false) return false;

  const photo = String(adherent.photo_url || adherent.photo || "").trim();

  return Boolean(photo);
}

function extractEmailsFromAdherent(adherent) {
  const fields = [
    "email",
    "mail",
    "email_parent",
    "email_responsable",
    "mail_parent",
    "mail_responsable",
    "email_pere",
    "email_mere",
    "mail_pere",
    "mail_mere",
  ];

  const emails = new Set();

  fields.forEach((field) => {
    const value = adherent?.[field];
    if (!value) return;

    String(value)
      .split(/[;,/ ]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((email) => {
        if (/.+@.+\..+/.test(email)) {
          emails.add(email);
        }
      });
  });

  return Array.from(emails);
}

const sorters = {
  alpha: (a, b) =>
    String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr", {
      sensitivity: "base",
    }) ||
    String(a?.prenom || "").localeCompare(String(b?.prenom || ""), "fr", {
      sensitivity: "base",
    }),

  "age-asc": (a, b) =>
    (calculateAge(a?.date_naissance) ?? 999) -
    (calculateAge(b?.date_naissance) ?? 999),

  "age-desc": (a, b) =>
    (calculateAge(b?.date_naissance) ?? -1) -
    (calculateAge(a?.date_naissance) ?? -1),

  statut: (a, b) =>
    String(a?.statut_inscription || "").localeCompare(
      String(b?.statut_inscription || ""),
      "fr",
      { sensitivity: "base" },
    ),
};

function normalizeFilters(rawFilters) {
  const next = {
    ...DEFAULT_FILTERS,
    ...rawFilters,
  };

  const selectedDojo = String(next.selectedDojo || next.dojoId || "");
  const filterGroup = String(next.filterGroup || next.groupName || "");
  const selectedSeason = String(next.selectedSeason || next.season || "");
  const statusFilter = String(
    next.statusFilter || next.statutInscription || "",
  );

  return {
    ...next,

    selectedDojo,
    dojoId: selectedDojo,

    filterGroup,
    groupName: filterGroup,

    selectedSeason,
    season: selectedSeason,

    statusFilter,
    statutInscription: statusFilter,

    hideStopped: Boolean(next.hideStopped ?? next.hideLicencieArret),
    hideLicencieArret: Boolean(next.hideStopped ?? next.hideLicencieArret),
  };
}

function formatDateForCsv(dateValue) {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("fr-FR");
}

export function useListingAdherents() {
  const [dojos, setDojos] = useState([]);
  const [allGroups, setAllGroups] = useState([]);

  const [adherents, setAdherents] = useState([]);
  const [birthdayAdherents, setBirthdayAdherents] = useState([]);

  const [seasons, setSeasons] = useState([]);

  const [filtersState, setFiltersState] = useState(DEFAULT_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("details");
  const [currentAdherent, setCurrentAdherent] = useState(null);

  const filters = useMemo(() => normalizeFilters(filtersState), [filtersState]);

  const setFilters = useCallback((updater) => {
    setFiltersState((previous) => {
      const prevNormalized = normalizeFilters(previous);

      const candidate =
        typeof updater === "function"
          ? updater(prevNormalized)
          : {
              ...prevNormalized,
              ...updater,
            };

      const nextNormalized = normalizeFilters(candidate);

      const dojoChanged =
        String(prevNormalized.selectedDojo || "") !==
        String(nextNormalized.selectedDojo || "");

      if (dojoChanged) {
        return normalizeFilters({
          ...nextNormalized,
          filterGroup: "",
          groupName: "",
        });
      }

      return nextNormalized;
    });
  }, []);

  const groups = useMemo(() => {
    if (!filters.selectedDojo) {
      return allGroups;
    }

    return allGroups.filter(
      (group) => String(group.dojo_id) === String(filters.selectedDojo),
    );
  }, [allGroups, filters.selectedDojo]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, dojoList, adherentList, birthdayList, groupList] =
        await Promise.all([
          getSettings(),
          getDojos(),
          getAllAdherentsListWithGroup(),
          getBirthdayAdherents(),
          getGroupsList(),
        ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const safeDojos = Array.isArray(dojoList) ? dojoList : [];
      const safeGroups = Array.isArray(groupList) ? groupList : [];
      const safeAdherents = Array.isArray(adherentList) ? adherentList : [];
      const safeBirthdays = Array.isArray(birthdayList) ? birthdayList : [];

      setDojos(safeDojos);
      setAllGroups(safeGroups);
      setAdherents(safeAdherents);
      setBirthdayAdherents(safeBirthdays);

      const activeSeason = settings?.saison_active || "";

      if (activeSeason) {
        const { prev, next } = seasonNeighbors(activeSeason);

        setSeasons([prev, activeSeason, next].filter(Boolean));
      }

      setFiltersState((previous) => {
        const nextFilters = normalizeFilters(previous);

        const autoDojo =
          safeDojos.length === 1
            ? String(safeDojos[0].id)
            : nextFilters.selectedDojo;

        return normalizeFilters({
          ...nextFilters,
          selectedDojo: autoDojo,
          dojoId: autoDojo,
          selectedSeason: nextFilters.selectedSeason || activeSeason,
          season: nextFilters.selectedSeason || activeSeason,
        });
      });
    } catch (error) {
      console.error("Erreur chargement listing adhérents :", error);
      toast.error("Erreur lors du chargement des adhérents.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [
    filters.search,
    filters.selectedDojo,
    filters.filterGroup,
    filters.selectedSeason,
    filters.statusFilter,
    filters.sortBy,
    filters.hideStopped,
    filters.onlyBlessed,
    filters.onlyWithoutPhoto,
  ]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const syncCurrentAdherent = useCallback((list) => {
    setCurrentAdherent((current) => {
      if (!current?.id) return current;

      return list.find((adherent) => adherent.id === current.id) || current;
    });
  }, []);

  const refreshAdherentsOnly = useCallback(async () => {
    try {
      const list = await getAllAdherentsListWithGroup();
      const safeList = Array.isArray(list) ? list : [];

      setAdherents(safeList);
      syncCurrentAdherent(safeList);
    } catch (error) {
      console.error("Erreur rechargement adhérents :", error);
      toast.error("Impossible de recharger la liste des adhérents.");
    }
  }, [syncCurrentAdherent]);

  const allowedGroupNames = useMemo(
    () => new Set((groups || []).map((group) => group.nom)),
    [groups],
  );

  const birthdaySet = useMemo(
    () => new Set((birthdayAdherents || []).map((adherent) => adherent.id)),
    [birthdayAdherents],
  );

  const filteredAdherents = useMemo(() => {
    const query = String(filters.search || "")
      .trim()
      .toLowerCase();

    const filtered = (adherents || []).filter((adherent) => {
      const groupLabel = getGroupsLabel(adherent);

      const matchesDojo =
        !filters.selectedDojo ||
        (adherent?.Groups || []).some((group) =>
          allowedGroupNames.has(group.nom),
        );

      const matchesGroup =
        !filters.filterGroup ||
        (adherent?.Groups || []).some(
          (group) => group.nom === filters.filterGroup,
        );

      const matchesSeason =
        !filters.selectedSeason ||
        adherent?.saison_active === filters.selectedSeason;

      const matchesSearch =
        !query ||
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.licence_number,
          adherent?.statut_inscription,
          groupLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesStatus =
        !filters.statusFilter ||
        adherent?.statut_inscription === filters.statusFilter;

      const matchesStopped =
        !filters.hideStopped ||
        !STOPPED_STATUSES.includes(adherent?.statut_inscription);

      const matchesBlessed = !filters.onlyBlessed || !!adherent?.is_blessed;

      const matchesPhoto =
        !filters.onlyWithoutPhoto || !hasUsablePhoto(adherent);

      return (
        matchesDojo &&
        matchesGroup &&
        matchesSeason &&
        matchesSearch &&
        matchesStatus &&
        matchesStopped &&
        matchesBlessed &&
        matchesPhoto
      );
    });

    const sorter = sorters[filters.sortBy] || sorters.alpha;

    return filtered.slice().sort(sorter);
  }, [adherents, filters, allowedGroupNames]);

  const displayedAdherents = useMemo(
    () => filteredAdherents.slice(0, visibleLimit),
    [filteredAdherents, visibleLimit],
  );

  const hasMore = displayedAdherents.length < filteredAdherents.length;

  const loadMore = useCallback(() => {
    setVisibleLimit((current) => current + PAGE_SIZE);
  }, []);

  const stats = useMemo(() => {
    const total = filteredAdherents.length;

    const blessed = filteredAdherents.filter(
      (adherent) => !!adherent?.is_blessed,
    ).length;

    const birthdays = filteredAdherents.filter((adherent) =>
      birthdaySet.has(adherent.id),
    ).length;

    const withoutPhoto = filteredAdherents.filter(
      (adherent) => !hasUsablePhoto(adherent),
    ).length;

    const stopped = filteredAdherents.filter((adherent) =>
      STOPPED_STATUSES.includes(adherent?.statut_inscription),
    ).length;

    const licensed = filteredAdherents.filter(
      (adherent) => adherent?.statut_inscription === "Licencié",
    ).length;

    const trial = filteredAdherents.filter(
      (adherent) => adherent?.statut_inscription === "Cours d'essai",
    ).length;

    return {
      total,
      totalAll: adherents.length,
      displayed: displayedAdherents.length,
      blessed,
      birthdays,
      withoutPhoto,
      stopped,
      licensed,
      trial,
    };
  }, [
    filteredAdherents,
    birthdaySet,
    adherents.length,
    displayedAdherents.length,
  ]);

  const openDrawer = useCallback((mode, adherent) => {
    setCurrentAdherent(adherent || null);
    setDrawerMode(mode);
    setDrawerOpen(true);
  }, []);

  const openDetailsDrawer = useCallback(
    (adherent) => openDrawer("details", adherent),
    [openDrawer],
  );

  const openEditDrawer = useCallback(
    (adherent) => openDrawer("edit", adherent),
    [openDrawer],
  );

  const openGroupsDrawer = useCallback(
    (adherent) => openDrawer("groups", adherent),
    [openDrawer],
  );

  const openPhotoDrawer = useCallback(
    (adherent) => openDrawer("photo", adherent),
    [openDrawer],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleUpdateAdherent = useCallback(
    async (payload = {}) => {
      if (!currentAdherent?.id) return;

      try {
        setSaving(true);

        await updateAdherent(currentAdherent.id, payload);

        toast.success("Adhérent mis à jour.");
        await refreshAdherentsOnly();

        setDrawerMode("details");
      } catch (error) {
        console.error("Erreur mise à jour adhérent :", error);
        toast.error("Erreur lors de la mise à jour de l’adhérent.");
      } finally {
        setSaving(false);
      }
    },
    [currentAdherent?.id, refreshAdherentsOnly],
  );

  const handleUpdatePhoto = useCallback(
    async (payload = {}) => {
      if (!currentAdherent?.id) return;

      const stayInMode = Boolean(payload?._stayInMode);

      try {
        setSaving(true);

        await updateAdherent(currentAdherent.id, {
          photo_url: payload.photo_url || "",
          autorisation_photo: payload.autorisation_photo ?? true,
        });

        toast.success("Photo mise à jour.");
        await refreshAdherentsOnly();

        if (!stayInMode) {
          setDrawerMode("details");
        }
      } catch (error) {
        console.error("Erreur mise à jour photo :", error);
        toast.error("Erreur lors de la mise à jour de la photo.");
      } finally {
        setSaving(false);
      }
    },
    [currentAdherent?.id, refreshAdherentsOnly],
  );

  const handleUpdateGroups = useCallback(
    async (selectedGroupIds = []) => {
      if (!currentAdherent?.id) return;

      try {
        setSaving(true);

        const currentIds = new Set(
          (currentAdherent?.Groups || [])
            .map((group) => group?.id)
            .filter(Boolean)
            .map(String),
        );

        const selectedIds = new Set(
          (selectedGroupIds || []).filter(Boolean).map((id) => String(id)),
        );

        const toAdd = [...selectedIds]
          .filter((id) => !currentIds.has(id))
          .map(Number);

        const toRemove = [...currentIds]
          .filter((id) => !selectedIds.has(id))
          .map(Number);

        await Promise.all([
          ...toAdd.map((groupId) =>
            addAdherentToGroup(currentAdherent.id, groupId),
          ),
          ...toRemove.map((groupId) =>
            removeAdherentFromGroup(currentAdherent.id, groupId),
          ),
        ]);

        toast.success(
          `Groupes mis à jour (${toAdd.length} ajout(s), ${toRemove.length} retrait(s)).`,
        );

        await refreshAdherentsOnly();
        setDrawerMode("details");
      } catch (error) {
        console.error("Erreur mise à jour groupes :", error);
        toast.error("Erreur lors de la mise à jour des groupes.");
      } finally {
        setSaving(false);
      }
    },
    [currentAdherent, refreshAdherentsOnly],
  );

  const handleUpdateTeacherNotes = useCallback(
    async (value) => {
      if (!currentAdherent?.id) return;

      const notes =
        typeof value === "string"
          ? value
          : (value?.notes_enseignant ?? value?.teacherNotes ?? "");

      try {
        setSaving(true);

        await updateTeacherNotes(currentAdherent.id, {
          notes_enseignant: notes,
        });

        toast.success("Notes enseignant mises à jour.");
        await refreshAdherentsOnly();
      } catch (error) {
        console.error("Erreur notes enseignant :", error);
        toast.error("Erreur lors de la mise à jour des notes enseignant.");
      } finally {
        setSaving(false);
      }
    },
    [currentAdherent?.id, refreshAdherentsOnly],
  );

  const handleUpdateBlessure = useCallback(
    async (payload = {}) => {
      if (!currentAdherent?.id) return;

      try {
        setSaving(true);

        await updateBlessures(currentAdherent.id, {
          is_blessed: !!payload.is_blessed,
          date_reprise: payload.is_blessed
            ? payload.date_reprise || null
            : null,
          notes_adherent: payload.is_blessed
            ? payload.notes_adherent || null
            : null,
        });

        toast.success("Blessure / absence mise à jour.");
        await refreshAdherentsOnly();
      } catch (error) {
        console.error("Erreur blessure :", error);
        toast.error("Erreur lors de la mise à jour blessure / absence.");
      } finally {
        setSaving(false);
      }
    },
    [currentAdherent?.id, refreshAdherentsOnly],
  );

  const handleDeleteAdherent = useCallback(
    async (adherentOrId) => {
      const adherent =
        typeof adherentOrId === "object"
          ? adherentOrId
          : adherents.find((item) => item.id === adherentOrId) ||
            currentAdherent;

      if (!adherent?.id) return;

      const label = `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();

      if (
        !window.confirm(
          `Supprimer ${label || "cet adhérent"} ? Cette action est irréversible.`,
        )
      ) {
        return;
      }

      try {
        setDeletingId(adherent.id);

        await deleteAdherent(adherent.id);

        toast.success("Adhérent supprimé.");

        setDrawerOpen(false);
        setCurrentAdherent(null);

        await refreshAdherentsOnly();
      } catch (error) {
        console.error("Erreur suppression adhérent :", error);
        toast.error("Erreur lors de la suppression.");
      } finally {
        setDeletingId(null);
      }
    },
    [adherents, currentAdherent, refreshAdherentsOnly],
  );

  const handleCopyEmails = useCallback(async (list = []) => {
    const emails = new Set();

    list.forEach((adherent) => {
      extractEmailsFromAdherent(adherent).forEach((email) => emails.add(email));
    });

    const value = Array.from(emails).join(";");

    if (!value) {
      toast.info("Aucun email à copier avec ces filtres.");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success("Emails copiés dans le presse-papiers.");
    } catch {
      toast.error("Impossible de copier les emails.");
    }
  }, []);

  const handleExportCsv = useCallback(
    (list = []) => {
      if (!list.length) {
        toast.info("Aucune donnée à exporter.");
        return;
      }

      const rows = [
        [
          "Nom",
          "Prénom",
          "Statut",
          "Saison",
          "Âge",
          "Date naissance",
          "Groupes",
          "Email(s)",
          "Téléphone",
          "Poids",
          "Blessé",
          "Date reprise",
        ].join(";"),

        ...list.map((adherent) => {
          const age = calculateAge(adherent?.date_naissance);

          return [
            adherent?.nom || "",
            adherent?.prenom || "",
            adherent?.statut_inscription || "",
            adherent?.saison_active || "",
            Number.isFinite(age) ? age : "",
            formatDateForCsv(adherent?.date_naissance),
            getGroupsLabel(adherent),
            extractEmailsFromAdherent(adherent).join(", "),
            adherent?.tel || adherent?.telephone || "",
            adherent?.poids || "",
            adherent?.is_blessed ? "Oui" : "Non",
            formatDateForCsv(adherent?.date_reprise),
          ]
            .map((value) => `"${String(value).replace(/"/g, '""')}"`)
            .join(";");
        }),
      ].join("\n");

      const blob = new Blob([`\ufeff${rows}`], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `adherents_${filters.filterGroup || "tous"}_${
        filters.selectedSeason || "saison"
      }.csv`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      toast.success("Export CSV généré.");
    },
    [filters.filterGroup, filters.selectedSeason],
  );

  const resetFilters = useCallback(() => {
    const activeSeason = seasons?.[1] || filters.selectedSeason || "";

    const autoDojo = dojos.length === 1 ? String(dojos[0].id) : "";

    setFiltersState(
      normalizeFilters({
        ...DEFAULT_FILTERS,
        selectedDojo: autoDojo,
        dojoId: autoDojo,
        selectedSeason: activeSeason,
        season: activeSeason,
      }),
    );
  }, [dojos, seasons, filters.selectedSeason]);

  return {
    adherents,
    filteredAdherents,
    displayedAdherents,
    stats,

    filters,
    setFilters,
    resetFilters,

    seasons,
    dojos,
    groups,
    allGroups,

    birthdayAdherents,
    birthdaySet,

    loading,
    saving,
    deletingId,

    hasMore,
    loadMore,

    drawerOpen,
    drawerMode,
    currentAdherent,

    openDetailsDrawer,
    openEditDrawer,
    openGroupsDrawer,
    openPhotoDrawer,
    closeDrawer,

    handleUpdateAdherent,
    handleUpdatePhoto,
    handleUpdateGroups,
    handleUpdateTeacherNotes,
    handleUpdateBlessure,
    handleDeleteAdherent,

    handleCopyEmails,
    handleExportCsv,
    handleExportCSV: handleExportCsv,

    refresh,
  };
}

export default useListingAdherents;
